/**
 * SEO component that queries for data with
 * Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/how-to/querying-data/use-static-query/
 */

import * as React from "react"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

function Seo({ description, title, children, path = "/", image = "", articleData = null }) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata?.description
  const defaultTitle = site.siteMetadata?.title
  const siteUrl = site.siteMetadata?.siteUrl || ''
  const ogImage = image || `${siteUrl}/og-image.jpg` // Default OG image
  const pageTitle = title ? `${title} | ${defaultTitle}` : defaultTitle

  // Schema.org structured data
  const schemaOrgWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: `${siteUrl}${path}`,
    headline: pageTitle,
    description: metaDescription,
    inLanguage: 'en-US',
    mainEntityOfPage: `${siteUrl}${path}`,
    name: pageTitle,
    author: {
      '@type': 'Organization',
      name: site.siteMetadata?.author || '',
    },
    publisher: {
      '@type': 'Organization',
      name: defaultTitle,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
  }

  // Add article schema if article data is provided
  const schemaArticle = articleData ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleData.title || pageTitle,
    image: ogImage,
    datePublished: articleData.publishDate,
    dateModified: articleData.modifiedDate || articleData.publishDate,
    author: {
      '@type': 'Person',
      name: articleData.author || site.siteMetadata?.author || '',
    },
    publisher: {
      '@type': 'Organization',
      name: defaultTitle,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
    description: metaDescription,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}${path}`,
    },
  } : null;

  return (
    <Helmet
      htmlAttributes={{ lang: 'en' }}
      title={pageTitle}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          name: `keywords`,
          content: `california, fish plants, fishing, maps, updates, fishing guides`,
        },
        {
          name: `robots`,
          content: `index, follow`,
        },
        {
          property: `og:title`,
          content: pageTitle,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: articleData ? 'article' : 'website',
        },
        {
          property: `og:url`,
          content: `${siteUrl}${path}`,
        },
        {
          property: `og:image`,
          content: ogImage,
        },
        {
          property: `og:image:width`,
          content: `1200`,
        },
        {
          property: `og:image:height`,
          content: `630`,
        },
        {
          property: `og:site_name`,
          content: defaultTitle,
        },
        {
          property: `og:locale`,
          content: `en_US`,
        },
        {
          name: `twitter:card`,
          content: `summary_large_image`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata?.author || ``,
        },
        {
          name: `twitter:title`,
          content: pageTitle,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `twitter:image`,
          content: ogImage,
        },
      ]}
      link={[
        {
          rel: "canonical",
          href: `${siteUrl}${path}`,
        },
      ]}
    >
      {/* Schema.org structured data */}
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgWebPage)}
      </script>
      
      {articleData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaArticle)}
        </script>
      )}
      
      {children}
    </Helmet>
  )
}

export default Seo
